'use client';

import { useMemo } from 'react';
import { Box, Grid, token } from '@terra/ui-styled-system';
import { Badge, BodyDefault } from '@terra/ui-design-system';
import {
  Globe02Icon,
  Shapes01Icon,
  Shield01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useTargetId } from '../../../store/projectStore';
import { useExternalNetworkStats } from '../../../hooks/api/useExternalNetworkStats';
import { useExternalNetworkSignalsTrend } from '../../../hooks/api/useExternalNetworkSignalsTrend';
import { useHomeVisitBaseline } from '../../../hooks/dashboard/useHomeVisitBaseline';
import { useThreadsWaitingForInput } from '../../../hooks/dashboard/useThreadsWaitingForInput';
import { usePersistTargetVisit } from '../../../hooks/usePersistTargetVisit';
import { TARGET_TYPE } from '../../../utils/target-scope.utils';
import { FindingsSeverityPostureCard } from '../../dashboard/FindingsSeverityPostureCard';
import { HomePageLayout } from '../../dashboard/HomePageLayout';
import { HomeWorkGrid } from '../../dashboard/HomeWorkGrid';
import { buildNeedsAttentionItems } from '../../dashboard/needs-attention-items';
import { fromSeverityBuckets } from '../../dashboard/posture-items';
import {
  SnapshotSinceLastVisitStrip,
  type SnapshotStripCell,
} from '../../dashboard/SnapshotSinceLastVisitStrip';
import type { LocalHealthIssue } from '../../dashboard/TargetHealthCard';
import { WelcomeHeader } from '../../dashboard/WelcomeHeader';
import { ExternalAssetsPostureCard } from './ExternalAssetsPostureCard';
import { SignalsTrendWidget } from './SignalsTrendWidget';

const MUTED_ICON_COLOR = token('colors.fg.neutral.muted');

const ACTIVE_SCAN_STATUSES = new Set([
  'DISCOVERING',
  'SCANNING',
  'ANALYZING',
  'DIFFING',
]);

const NO_LOCAL_HEALTH_ISSUES: LocalHealthIssue[] = [];

const sumCounts = (counts: Record<string, number> | undefined): number =>
  counts ? Object.values(counts).reduce((sum, count) => sum + count, 0) : 0;

export function ExternalNetworkOverviewTab() {
  const t = useTranslations('home');
  const surfaceId = useTargetId();
  const targetId = surfaceId ?? null;
  const targetType = TARGET_TYPE.EXTERNAL_NETWORK;
  const {
    baselineAt,
    isReady: isBaselineReady,
    previousSnapshot: previousMetrics,
  } = useHomeVisitBaseline({ targetId, targetType });
  const { stats, isLoading: isStatsLoading } =
    useExternalNetworkStats(targetId);
  const { trend, isLoading: isTrendLoading } = useExternalNetworkSignalsTrend({
    surfaceId: targetId,
  });
  const threadsWaitingForInput = useThreadsWaitingForInput(surfaceId ?? '');

  const assetCounts = useMemo(
    () =>
      stats
        ? {
            hosts: stats.totalHosts,
            domains: stats.totalDomains,
            services: stats.totalServices,
            gateways: stats.totalGateways,
            netblocks: stats.totalNetblocks,
            software: stats.totalSoftware,
          }
        : undefined,
    [stats],
  );
  const severityCounts = useMemo(
    () =>
      stats ? fromSeverityBuckets(stats.findingsCountsBySeverity) : undefined,
    [stats],
  );
  const totalAssets = sumCounts(assetCounts);
  const openFindingsCount = sumCounts(severityCounts);
  const signalsTotal = stats?.totalSignals ?? 0;
  const domainsInScope = stats?.domainsInScope ?? 0;

  const visitSnapshot = useMemo(() => {
    if (!surfaceId || !stats) {
      return null;
    }

    return {
      signalsCount: signalsTotal,
      findingsCount: openFindingsCount,
      assetsCount: totalAssets,
      domainsInScope,
    };
  }, [
    surfaceId,
    stats,
    signalsTotal,
    openFindingsCount,
    totalAssets,
    domainsInScope,
  ]);

  usePersistTargetVisit({ targetId, targetType, snapshot: visitSnapshot });

  if (!surfaceId) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BodyDefault color="fg.neutral.muted">
          {t('selectExternalNetwork')}
        </BodyDefault>
      </Box>
    );
  }

  const attentionItems = buildNeedsAttentionItems({
    counts: {
      lastScanFailed: stats?.lastScanStatus === 'FAILED' ? 1 : 0,
      waitingForInput: threadsWaitingForInput.length,
      criticalFindings: severityCounts?.Critical ?? 0,
    },
    waitingForInputThreadId: threadsWaitingForInput[0] ?? null,
  });
  const isScanRunning = stats?.lastScanStatus
    ? ACTIVE_SCAN_STATUSES.has(stats.lastScanStatus)
    : false;
  const lastScanDetail = stats?.lastScanAt
    ? t('health.lastScan', {
        relativeTime: formatDistanceToNowStrict(new Date(stats.lastScanAt), {
          addSuffix: true,
        }),
      })
    : t('health.noScanYet');

  const stripCells: SnapshotStripCell[] = [
    {
      key: 'assets',
      label: t('sinceLastVisit.assets'),
      icon: <Shapes01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: totalAssets,
      previous: previousMetrics?.assetsCount,
      destination: { kind: 'list', destination: { list: 'assets' } },
    },
    {
      key: 'signals',
      label: t('sinceLastVisit.signals'),
      icon: <Target03Icon size={16} color={MUTED_ICON_COLOR} />,
      current: signalsTotal,
      previous: previousMetrics?.signalsCount,
      destination: { kind: 'list', destination: { list: 'signals' } },
    },
    {
      key: 'domainsInScope',
      label: t('sinceLastVisit.domainsInScope'),
      icon: <Globe02Icon size={16} color={MUTED_ICON_COLOR} />,
      current: domainsInScope,
      previous: previousMetrics?.domainsInScope,
    },
    {
      key: 'findings',
      label: t('sinceLastVisit.openFindings'),
      icon: <Shield01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: openFindingsCount,
      previous: previousMetrics?.findingsCount,
      destination: { kind: 'list', destination: { list: 'findings' } },
    },
  ];

  return (
    <HomePageLayout>
      <WelcomeHeader
        lastVisitAt={isBaselineReady ? baselineAt : undefined}
        status={
          isScanRunning ? (
            <Badge variant="outline" size="sm" colorPalette="blue">
              {t('networkActivity.scanRunning')}
            </Badge>
          ) : null
        }
      />
      <SnapshotSinceLastVisitStrip
        applicationId={surfaceId}
        cells={stripCells}
        isLoading={isStatsLoading || !isBaselineReady}
      />
      <HomeWorkGrid
        applicationId={surfaceId}
        attentionItems={attentionItems}
        isAttentionLoading={isStatsLoading}
        localHealthIssues={NO_LOCAL_HEALTH_ISSUES}
        healthyDetail={isStatsLoading ? undefined : lastScanDetail}
      />
      <Grid w="full" columns={{ base: 1, lg: 2 }} gap={4} alignItems="stretch">
        <FindingsSeverityPostureCard
          applicationId={surfaceId}
          counts={severityCounts}
          isLoading={isStatsLoading}
        />
        <ExternalAssetsPostureCard
          surfaceId={surfaceId}
          counts={assetCounts}
          isLoading={isStatsLoading}
        />
      </Grid>
      <SignalsTrendWidget
        title={t('networkActivity.signalsTrend')}
        dailyCounts={trend?.dailyData ?? []}
        isLoading={isTrendLoading}
      />
    </HomePageLayout>
  );
}
